import { Injectable } from '@angular/core';
import { PaymentType } from '@app/shared/enums/payment-type';
import { HardwareProduct } from '@app/shared/models/hardware-product.model';
import { Price } from '@app/shared/models/price.model';
import { Equipment } from '../models/order-capture.model';
import { PaymentOptionType, SubscriptionBundleOption } from '../modules/mobilephone/models/mobilephone-search-item';
import {
  AccessoryItem,
  HardwareOfferingWithSubscriptionPrice,
  ProductTypes,
  SubscriptionBindingInfo,
  SubscriptionForHardwareOffering,
} from '../models/accessory.model';
import { mapPriceToAmountPrice } from '@app/core/price-utils';
import { VoiceSubscriptionOffering } from '../modules/subscription/models/offerings-response.model';
import { HardwareVariantOfferingDetails } from '../modules/accessories/models/hardware-offering-model';
import { AccessoryForm } from '../modules/equipment/equipment-dialog/equipment-forms/manual-registration-accessories-form/manual-registration-accessories-form.component';
import { FormGroup } from '@angular/forms';
import { HardwareCategory } from '@app/shared/models/product.model';
import { BindingType } from '@app/shared/models/subscription-offering.model';

export type HardwareOfferingSubscriptionData = {
  subscription: SubscriptionForHardwareOffering;
  availableSubscriptions: SubscriptionForHardwareOffering[];
};

@Injectable({
  providedIn: 'root',
})
export class AccessoryProductHelperService {
  constructor() {}

  static getAccessoryProductPrice(accessoryProduct: HardwareProduct): Price {
    if (!!accessoryProduct.selectedSubscription?.hardwareOfferingPriceWithSubscription) {
      const { amountIncludingVAT, amountExcludingVAT, vatPercentage } =
        accessoryProduct.selectedSubscription.hardwareOfferingPriceWithSubscription;

      return {
        includingVAT: amountIncludingVAT,
        excludingVAT: amountExcludingVAT,
        vatPercentage: vatPercentage,
      } as Price;
    } else {
      return accessoryProduct.equipment.price;
    }
  }

  static getAccessoryPaymentType(hardwareProduct: HardwareProduct): PaymentType {
    // If manually registered, the hardware has to be bound to a subscription, hence 'PaymentType.Billing'
    return hardwareProduct.equipment.isManualRegistration ? PaymentType.Billing : PaymentType.Upfront;
  }

  static mapSubscriptionBundleOptionToSubscriptionForHardwareOffering(
    hardware: Equipment,
    bundle: SubscriptionBundleOption
  ): SubscriptionForHardwareOffering {
    return {
      hardwareOfferingId: hardware.offeringId,
      subscriptionOfferingId: bundle.subscriptionOfferingId,
      subsidyId: bundle.subsidyId,
      hardwareOfferingPriceWithSubscription: mapPriceToAmountPrice(bundle.priceInformation.price),
      subscriptionMonthlyPrice: mapPriceToAmountPrice(bundle.subscriptionPriceInformation.price),
      subscriptionFullName: bundle.subscriptionName,
      subscriptionName: bundle.subscriptionShortName,
      bindingInfo: { bindingPeriodInMonths: bundle.subscriptionBindingPeriod } as SubscriptionBindingInfo,
      isAvailableWithEquipmentUpgrade: false,
    } as SubscriptionForHardwareOffering;
  }

  static mapVoiceSubscriptionOfferingToSubscriptionForHardwareOffering(
    hardware: Equipment,
    voiceOffering: VoiceSubscriptionOffering
  ): SubscriptionForHardwareOffering {
    const manualRegistrationBinding = voiceOffering?.bindings.find(vo => vo.bindingType === BindingType.Handset);

    return {
      hardwareOfferingId: hardware.offeringId,
      subscriptionOfferingId: voiceOffering.id,
      subsidyId: null,
      hardwareOfferingPriceWithSubscription: hardware.price ? mapPriceToAmountPrice(hardware.price) : null,
      subscriptionMonthlyPrice: mapPriceToAmountPrice(voiceOffering.monthlyPrice.price),
      subscriptionFullName: voiceOffering.name,
      subscriptionName: voiceOffering.shortName,
      bindingInfo: {
        bindingPeriodInMonths: manualRegistrationBinding?.period,
        terminationFee: manualRegistrationBinding?.terminationFee,
      } as SubscriptionBindingInfo,
      isAvailableWithEquipmentUpgrade: false,
    } as SubscriptionForHardwareOffering;
  }

  static createEquipment(hardware: HardwareVariantOfferingDetails, accessoryForm: FormGroup<AccessoryForm>): Equipment {
    const { productName, gtin, imei } = accessoryForm.controls;

    let equipment = new Equipment();
    equipment.offeringId = hardware?.offeringId;
    equipment.name = hardware?.name || productName.value;
    equipment.gtin = hardware?.gtin || gtin.value;
    equipment.imei = imei.value;
    equipment.price = hardware?.priceInformation?.price;
    equipment.image = hardware?.image?.downloadUri;
    equipment.imageUrl = hardware?.image?.downloadUri;
    equipment.isManualRegistration = true;
    return equipment;
  }

  static createSubscriptionDataForHardwareOffering(
    selectedSubscriptionId: string,
    subscriptionOptions: SubscriptionBundleOption[] | VoiceSubscriptionOffering[],
    equipment: Equipment
  ): HardwareOfferingSubscriptionData {
    let subscription: SubscriptionForHardwareOffering;
    let availableSubscriptions: SubscriptionForHardwareOffering[];
    const {
      mapSubscriptionBundleOptionToSubscriptionForHardwareOffering,
      mapVoiceSubscriptionOfferingToSubscriptionForHardwareOffering,
    } = AccessoryProductHelperService;

    if ((subscriptionOptions[0] as SubscriptionBundleOption)?.type === PaymentOptionType.SubscriptionBundle) {
      const selectedSubscription = (subscriptionOptions as SubscriptionBundleOption[]).find(
        sub => sub.subscriptionOfferingId === selectedSubscriptionId
      );

      subscription = mapSubscriptionBundleOptionToSubscriptionForHardwareOffering(equipment, selectedSubscription);
      availableSubscriptions = subscriptionOptions.map(sub => {
        return mapSubscriptionBundleOptionToSubscriptionForHardwareOffering(equipment, sub);
      });
    } else {
      const selectedSubscription = (subscriptionOptions as VoiceSubscriptionOffering[]).find(
        offering => offering.id === selectedSubscriptionId
      );

      subscription = mapVoiceSubscriptionOfferingToSubscriptionForHardwareOffering(equipment, selectedSubscription);
      availableSubscriptions = subscriptionOptions.map(sub => {
        return mapVoiceSubscriptionOfferingToSubscriptionForHardwareOffering(equipment, sub);
      });
    }

    return { subscription, availableSubscriptions };
  }

  static createAccessoryItem(
    hardware: HardwareVariantOfferingDetails,
    availableSubscriptions: SubscriptionForHardwareOffering[],
    customProductName?: string
  ): AccessoryItem {
    const hardwareOfferingWithSubscriptionPrice = {
      offeringId: hardware?.offeringId,
      currentPrice: hardware?.priceInformation?.price,
      availableSubscriptions: availableSubscriptions,
    } as HardwareOfferingWithSubscriptionPrice;

    const accessoryItem = {
      offeringId: hardware?.offeringId,
      productType: ProductTypes[hardware?.productType],
      slug: hardware?.variantSlug,
      name: hardware?.name || customProductName,
      priceInformation: hardware?.priceInformation,
      gtin: hardware?.gtin,
      mainCategory: hardware?.mainCategory,
      subCategory: '',
      manufacturer: hardware?.manufacturer,
      image: hardware?.image,
      hardwareOfferingWithSubscriptionPrice: hardwareOfferingWithSubscriptionPrice,
      subscriptionBundleOptions: hardware?.paymentInformation?.subscriptionBundleOptions,
      priceFrom: hardware?.priceFrom,
      keyInformation: hardware?.keyInformation,
    } as AccessoryItem;

    return accessoryItem;
  }

  static createHardwareProduct(
    hardware: HardwareVariantOfferingDetails,
    subscription: SubscriptionForHardwareOffering,
    availableSubscriptions: SubscriptionForHardwareOffering[],
    accessoryForm: FormGroup<AccessoryForm>
  ): HardwareProduct {
    const { paymentMethod, subscriptionIdToUpgrade, productName } = accessoryForm.controls;

    let hardwareProduct = {} as HardwareProduct;
    hardwareProduct.equipment = AccessoryProductHelperService.createEquipment(hardware, accessoryForm);
    hardwareProduct.hardwareCategory = HardwareCategory[hardware?.productType];
    hardwareProduct.paymentType = paymentMethod.value;
    hardwareProduct.selectedSubscription = subscription;
    hardwareProduct.subscriptionIdToUpgrade = subscriptionIdToUpgrade.value;
    hardwareProduct.accessoryVariant = AccessoryProductHelperService.createAccessoryItem(
      hardware,
      availableSubscriptions,
      productName.value
    );
    hardwareProduct.subscriptionOfferingId = subscription.subscriptionOfferingId;
    return hardwareProduct;
  }
}
